import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { triggerLevantaSync, triggerAmazonSync, triggerImpactSync, reindexAlgolia } from "@/lib/affiliate.functions";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/affiliates")({
  head: () => ({
    meta: [
      { title: "Affiliate Sync — Admin" },
      { name: "description", content: "Trigger and monitor affiliate catalog sync runs." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AffiliatesAdmin,
});

type SyncRun = {
  id: string;
  network: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  products_seen: number;
  products_upserted: number;
  products_skipped: number;
  error: string | null;
};

function AffiliatesAdmin() {
  const qc = useQueryClient();
  const sync = useServerFn(triggerLevantaSync);
  const amazonSync = useServerFn(triggerAmazonSync);
  const impactSync = useServerFn(triggerImpactSync);
  const algolia = useServerFn(reindexAlgolia);

  const runs = useQuery({
    queryKey: ["sync-runs"],
    queryFn: async () => {
        const { data, error } = await supabase
          .from("sync_runs")
          .select("*")
          .order("started_at", { ascending: false })
          .limit(20);
        if (error || !data) return [] as SyncRun[];
        return data as SyncRun[];
    },
    refetchInterval: 5000,
  });

  const totals = useQuery({
    queryKey: ["products-count-by-network"],
    queryFn: async () => {
      const [levanta, amazon, impact] = await Promise.all([
        supabase.from("products").select("*", { count: "exact", head: true }).eq("network", "Levanta"),
        supabase.from("products").select("*", { count: "exact", head: true }).eq("network", "Amazon"),
        supabase.from("products").select("*", { count: "exact", head: true }).eq("network", "Impact"),
      ]);
      return {
        levanta: levanta.count ?? 0,
        amazon: amazon.count ?? 0,
        impact: impact.count ?? 0,
      };
    },
  });

  const lastSyncFor = (network: string) => {
    const run = runs.data?.find((r) => r.network === network && r.status === "success");
    return run ? new Date(run.started_at).toLocaleString() : "Never";
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold mt-2">Affiliate sync</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Pull product catalogs from connected affiliate networks.
          </p>
        </div>
      </div>

      <SyncCard
        name="Levanta"
        description="Levanta affiliate product catalog."
        productCount={totals.data?.levanta}
        lastSync={lastSyncFor("Levanta")}
        onSync={async () => {
          await sync({ data: undefined } as any);
        }}
      />
      <SyncCard
        name="Amazon"
        description="Amazon Product Advertising API 5.0. Requires AMAZON_ACCESS_KEY, AMAZON_SECRET_KEY, AMAZON_PARTNER_TAG."
        productCount={totals.data?.amazon}
        lastSync={lastSyncFor("Amazon")}
        onSync={async () => {
          const r = await amazonSync({ data: undefined } as any);
          if (!r.ok) toast.error(r.error ?? "Sync failed");
          else toast.success(`Synced ${r.upserted}/${r.seen} products`);
          qc.invalidateQueries({ queryKey: ["sync-runs"] });
          qc.invalidateQueries({ queryKey: ["products-count-by-network"] });
        }}
      />
      <SyncCard
        name="Impact"
        description="Impact.com Mediapartners catalog. Requires IMPACT_SID, IMPACT_AUTH_TOKEN, IMPACT_CATALOG_IDS (comma-separated catalog IDs, one per advertiser e.g. Airalo)."
        productCount={totals.data?.impact}
        lastSync={lastSyncFor("Impact")}
        onSync={async () => {
          const r = await impactSync({ data: undefined } as any);
          if (!r.ok) toast.error(r.error ?? "Sync failed");
          else toast.success(`Synced ${r.upserted}/${r.seen} products`);
          qc.invalidateQueries({ queryKey: ["sync-runs"] });
          qc.invalidateQueries({ queryKey: ["products-count-by-network"] });
        }}
      />
      <SyncCard
        name="Algolia"
        description="Push the products table into Algolia. Requires ALGOLIA_APP_ID and ALGOLIA_ADMIN_KEY."
        ctaLabel="Reindex now"
        lastSync={lastSyncFor("Algolia")}
        onSync={async () => {
          const r = await algolia({ data: undefined } as any);
          if (!r.ok) toast.error(r.error ?? "Reindex failed");
          else toast.success(`Indexed ${r.count} products`);
          qc.invalidateQueries({ queryKey: ["sync-runs"] });
        }}
      />

      <section className="space-y-3">
        <h2 className="font-semibold">Recent runs</h2>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2">Network</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Started</th>
                <th className="px-4 py-2 text-right">Seen</th>
                <th className="px-4 py-2 text-right">Upserted</th>
                <th className="px-4 py-2 text-right">Skipped</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.length ? (
                runs.data.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-4 py-2">{r.network}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          r.status === "success"
                            ? "text-green-600"
                            : r.status === "failed"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }
                        title={r.error ?? undefined}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right">{r.products_seen}</td>
                    <td className="px-4 py-2 text-right">{r.products_upserted}</td>
                    <td className="px-4 py-2 text-right">{r.products_skipped}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    No syncs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SyncCard({
  name,
  description,
  ctaLabel = "Sync now",
  productCount,
  lastSync,
  onSync,
}: {
  name: string;
  description: string;
  ctaLabel?: string;
  productCount?: number;
  lastSync: string;
  onSync: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="rounded-lg border bg-card p-4 sm:p-6 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="space-y-1">
          <h2 className="font-semibold">{name}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-muted-foreground">
            <span>Last sync: <span className="text-foreground">{lastSync}</span></span>
            {productCount !== undefined && (
              <span>Products synced: <span className="text-foreground">{productCount.toLocaleString()}</span></span>
            )}
          </div>
        </div>
        <button
          onClick={async () => {
            setError(null);
            setBusy(true);
            try {
              await onSync();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 shrink-0"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {ctaLabel}
        </button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </section>
  );
}